import { useEffect, useRef, useCallback } from 'react';
import { postEvent } from '../api/analytics';

// P5-2: 报告页埋点 hook。
//
// 范围（P5 锁定方案）：
// - 只埋 page_view / duration / report_complete 三类；维度卡片展开 / Tab 切换 /
//   CTA 点击留 P6（避免 schema 一上来就要为未知事件留 payload jsonb）。
// - session_id 走 sessionStorage（关 tab 即失效，跨日访问不污染完读率）。
// - duration 一律以 client_ts 差值算；上报时打 client_ts，server_ts 由后端 auto。
// - 离开 tab / 关闭页面用 fetch keepalive（postEvent 内部封装）；失败仅
//   "内存队列重试 1 次即丢"，不落 localStorage（不值这复杂度）。
// - report_complete dedup 双保险：
//     a) 前端 sessionStorage flag report_complete_<assessmentId> 已置 → 不发；
//     b) 后端 UniqueConstraint(session_id, assessment) where event_type=
//        report_complete 触发 → 204 幂等。

const SESSION_KEY = 'aiq_report_session_id';
const COMPLETE_FLAG_PREFIX = 'aiq_report_complete_';

function ensureSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage 不可用（隐私模式 / iframe 限制）→ 退到内存常量，
    // 同 tab 内一致，跨 tab 不一致；不阻塞主流程。
    return `mem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

async function sendWithRetry(payload) {
  // 内存队列重试 1 次即丢 — 不落 localStorage。
  try {
    const res = await postEvent(payload);
    if (res.ok || res.status === 204) return;
  } catch {
    // first-attempt failure — fall through to single retry
  }
  try {
    await postEvent(payload);
  } catch {
    // 最终失败：静默 + console.warn（best-effort，不阻塞主流程）。
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[useReportTracking] event drop after retry', payload.event_type);
    }
  }
}

function nowIso() {
  return new Date().toISOString();
}

/**
 * @param {object} args
 * @param {string|number} args.assessmentId  必填，主分析维度
 * @param {object|null}   args.currentPage   {pageType: 'overview'|'profile'|'dimension'|'final', dimId?: number} | null
 * @param {boolean}       args.enabled       报告未加载完 / 错误态时为 false
 */
export default function useReportTracking({ assessmentId, currentPage, enabled }) {
  const sessionIdRef = useRef('');
  const enterTsRef = useRef(0);
  const lastPageRef = useRef(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current = ensureSessionId();
  }

  const flushDuration = useCallback(() => {
    const last = lastPageRef.current;
    const enterTs = enterTsRef.current;
    if (!last || !enterTs || !assessmentId) return;
    const durationMs = Math.max(0, Date.now() - enterTs);
    if (durationMs < 250) return; // < 250ms 视为穿越（用户连按 ←/→），不上报
    sendWithRetry({
      session_id: sessionIdRef.current,
      assessment_id: Number(assessmentId),
      event_type: 'duration',
      page_type: last.pageType,
      dim_id: last.dimId ?? null,
      duration_ms: durationMs,
      client_ts: nowIso(),
    });
  }, [assessmentId]);

  // 页切换：先把上一页的 duration flush，再发新页 page_view
  useEffect(() => {
    if (!enabled || !assessmentId || !currentPage) return;

    flushDuration();

    enterTsRef.current = Date.now();
    lastPageRef.current = currentPage;

    sendWithRetry({
      session_id: sessionIdRef.current,
      assessment_id: Number(assessmentId),
      event_type: 'page_view',
      page_type: currentPage.pageType,
      dim_id: currentPage.dimId ?? null,
      client_ts: nowIso(),
    });
    // currentPage 引用变化即视为切页；caller 负责 useMemo 稳定性
  }, [enabled, assessmentId, currentPage, flushDuration]);

  // tab 隐藏 / 关闭：把当前页 duration 收尾发出
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushDuration();
        // 重置 enterTs 防止"重新可见 → 当前 - 旧 enter"误算
        enterTsRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        // 切回前台：重新打 enterTs，把后台 idle 时间从 dwell 里剔除。
        // 否则下次切页时 flushDuration 会把 [hidden→切页] 区间算进上一页。
        enterTsRef.current = Date.now();
      }
    };
    const onPageHide = () => flushDuration();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [enabled, flushDuration]);

  // 卸载（路由跳走）：把当前页 duration flush
  useEffect(() => {
    return () => flushDuration();
  }, [flushDuration]);

  // report_complete：调用方在末页挂载 / PDF 下载点击时调
  const reportComplete = useCallback(() => {
    if (!enabled || !assessmentId) return;
    const flagKey = `${COMPLETE_FLAG_PREFIX}${assessmentId}`;
    try {
      if (sessionStorage.getItem(flagKey)) return; // session 内只发一次
      sessionStorage.setItem(flagKey, '1');
    } catch {
      // sessionStorage 不可用 → 后端 UniqueConstraint 兜底
    }
    sendWithRetry({
      session_id: sessionIdRef.current,
      assessment_id: Number(assessmentId),
      event_type: 'report_complete',
      page_type: null,
      client_ts: nowIso(),
    });
  }, [enabled, assessmentId]);

  return { reportComplete };
}
